import { useQuery } from 'react-query';

import { resolveBearerToken } from '@/api/backendAuth';
import {
  fetchWorkspaceMembersFromBackend,
  type FetchWorkspaceMembersOptions,
} from '@/api/workspaceMembersApi';
import { useAppSelector } from '@/app/hooks';
import { AudienceByWorkspaceResponse } from '@/models/AudienceByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export type UseGetAudienceByWorkspaceOptions = FetchWorkspaceMembersOptions;

/**
 * Loads audience for the current workspace (same paging contract as activity logs: limit/offset, refetch behavior).
 * Default `mode: 'paged'`. Use `mode: 'all'` for pickers — one request capped at 500 rows (same max as activity logs).
 */
export function useGetAudienceByWorkspace(options?: UseGetAudienceByWorkspaceOptions) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);

  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3);
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const hasBearer = Boolean(resolveBearerToken(sessionJwt));

  const mode = options?.mode ?? 'paged';
  const pageIndex = options?.pageIndex ?? 0;
  const pageSize = options?.pageSize ?? 10;
  const search = options?.search ?? '';
  const sortBy = options?.sortBy ?? 'email';
  const sortDir = options?.sortDir ?? 'asc';

  return useQuery<AudienceByWorkspaceResponse>({
    queryKey:
      mode === 'all'
        ? [queryKeys.users, workspaceId, hasBearer, 'all']
        : [queryKeys.users, workspaceId, hasBearer, 'paged', pageIndex, pageSize, search, sortBy, sortDir],
    queryFn: () =>
      fetchWorkspaceMembersFromBackend(
        workspaceId,
        sessionJwt,
        mode === 'all'
          ? { mode: 'all' }
          : { mode: 'paged', pageIndex, pageSize, search, sortBy, sortDir },
      ),
    enabled: Boolean(workspaceId && user && hasBearer),
    staleTime: 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
